/**
 * Launch — the brief, sign-in AND the scan on ONE screen.
 *
 * Deliberately merged (Journey spec §02): sign-in and scan are a single atomic
 * action, so an "impossible state" like a signed-in account with an unscanned
 * interview can never exist. The auth form is EMBEDDED in the right-hand card
 * rather than a navigation to /auth — the flow's one previous dead end was
 * exactly that round-trip, which returned the user to a screen identical to
 * the one they left and relied on localStorage breadcrumbs to resume.
 *
 * The brief is assembled by string concatenation from the interview answers —
 * no LLM call. It is free, instant, and cannot hallucinate the founder's idea
 * back at them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BRIEF_SECTIONS,
  INTERVIEW_QUESTIONS,
  SKIPPED_BADGE,
  SKIPPED_SECTION_TEXT,
} from '@ideabrief/core';
import { Button, Logo, Mono, Wordmark } from '../components/ui';
import { ScanningMark } from '../components/ScanningMark';
import { AuthForm, type AuthMode } from '../components/AuthForm';
import { clearDraft, loadDraft, saveDraft } from '../lib/draft';
import { COHORT_SIZE } from './landing-content';

interface Props {
  signedIn: boolean;
}

export function Launch({ signedIn }: Props) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(loadDraft);
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  // Embedded auth card state. Sign-up first: someone finishing the interview
  // almost never has an account yet.
  const [authMode, setAuthMode] = useState<AuthMode>('up');
  const [authForgot, setAuthForgot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  /** In-flight latch for POST /api/scan — see submit(). */
  const runningRef = useRef(false);

  // Nothing to summarise without answers — send them back to the interview
  // rather than rendering an empty brief.
  useEffect(() => {
    if (Object.keys(draft.answers).length === 0) navigate('/capture', { replace: true });
  }, [draft.answers, navigate]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const sections = useMemo(
    () =>
      BRIEF_SECTIONS.map((section) => {
        const override = draft.overrides[section.key];
        if (override !== undefined) {
          return { ...section, text: override, skipped: false };
        }
        const parts = section.from.map((id) => draft.answers[id]);
        const skipped = parts.every((p) => !p || p.skipped);
        return {
          ...section,
          text: parts
            .filter((p) => p && !p.skipped)
            .map((p) => p?.answer)
            .join(' '),
          skipped,
        };
      }),
    [draft],
  );

  const oneliner =
    draft.overrides.oneliner ?? draft.answers.oneliner?.answer ?? '';

  function saveEdit(key: string) {
    const next = {
      ...draft,
      overrides: { ...draft.overrides, [key]: editText.trim() },
    };
    setDraft(next);
    saveDraft({ step: next.step, answers: next.answers, overrides: next.overrides });
    setEditing(null);
    setToast('Summary updated');
  }

  /**
   * POST the interview to /api/scan. The gates (session, consent) are the
   * CALLER's responsibility: the signed-in card checks its checkbox first, and
   * the embedded auth form has already collected consent by the time
   * onAuthenticated fires. This split is what lets auth-then-scan run as one
   * action without waiting for the `signedIn` prop, which lags a useSession
   * refetch behind the cookie that was just set.
   */
  const runScan = useCallback(async function runScan() {
    // Guards against a double submit (StrictMode double-invoke, or a re-render
    // landing between setSubmitting and the fetch). A second POST would burn
    // another ~144 neurons and create a duplicate idea.
    if (runningRef.current) return;
    runningRef.current = true;

    setError(null);
    setSubmitting(true);
    try {
      const answers = INTERVIEW_QUESTIONS.map((q) => {
        const a = draft.answers[q.id];
        return {
          questionId: q.id,
          answer: a?.skipped ? '' : (a?.answer ?? ''),
          skipped: a?.skipped ?? !a,
        };
      }).filter((a) => a.answer || a.skipped);

      const res = await fetch('/api/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, brief: draft.overrides }),
      });

      const body = (await res.json()) as
        | { ideaId: string }
        | { error: { message: string } };

      if (!res.ok) {
        setError(
          'error' in body
            ? body.error.message
            : 'Something went wrong. Your draft is saved — try again.',
        );
        setSubmitting(false);
        // Released on every failure path, otherwise the retry the error message
        // invites would be silently swallowed.
        runningRef.current = false;
        return;
      }

      // Only clear the local draft once the server owns it, so a failed scan
      // never loses eight answers.
      clearDraft();
      // `justScanned` tells the dashboard this idea has only just been created,
      // so it plays the reveal (bars building, queries typing in) rather than
      // snapping straight to a finished screen. Revisiting the URL later has no
      // state and renders instantly, which is correct — the reveal belongs to
      // the moment of creation, not to every future visit.
      navigate(`/idea/${(body as { ideaId: string }).ideaId}`, {
        replace: true,
        state: { justScanned: true },
      });
    } catch {
      setError('Something went wrong. Your draft is saved — try again.');
      setSubmitting(false);
      runningRef.current = false;
    }
  }, [draft, navigate]);

  /** The signed-in card's button: consent gate, then the scan. */
  function submitSignedIn() {
    setError(null);
    if (!consent) {
      setConsentError(true);
      return;
    }
    void runScan();
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'rgba(251,250,248,.86)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--line2)',
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: '0 auto',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Logo />
          <Wordmark />
          <div style={{ flex: 1 }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: 'var(--green)',
              }}
            />
            <Mono size={10.5}>DRAFT SAVED</Mono>
          </span>
          <Mono size={10.5}>STEP 2 OF 3</Mono>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px 80px' }}>
        <Mono size={11}>INTERVIEW COMPLETE · YOUR BRIEF IS READY</Mono>

        <h1
          style={{
            fontSize: 36,
            fontWeight: 600,
            lineHeight: 1.12,
            letterSpacing: '-.03em',
            margin: '12px 0 0',
            maxWidth: '20ch',
          }}
        >
          Your brief is ready. See where you stand.
        </h1>

        <p
          style={{
            fontSize: 16.5,
            color: 'var(--muted)',
            lineHeight: 1.6,
            margin: '12px 0 0',
            maxWidth: '58ch',
          }}
        >
          {signedIn
            ? `We scan it against ${COHORT_SIZE} real companies whose outcomes we know — your percentile in about 40 seconds. Glance over the brief and fix anything we got wrong.`
            : `Create your account and we scan it against ${COHORT_SIZE} real companies whose outcomes we know — your percentile in about 40 seconds. Glance over the brief and fix anything we got wrong.`}
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
            gap: 24,
            marginTop: 28,
            alignItems: 'start',
          }}
        >
          {/* Brief */}
          <div
            style={{
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(24,23,32,.05)',
            }}
          >
            <div
              style={{
                background: 'var(--acc-soft)',
                borderBottom: '1px solid var(--acc-line)',
                padding: '16px 20px',
              }}
            >
              <Mono size={10.5}>YOUR BRIEF · THE ONE-LINER</Mono>
              <EditableRow
                editing={editing === 'oneliner'}
                text={oneliner}
                value={editText}
                onValue={setEditText}
                onEdit={() => {
                  setEditing('oneliner');
                  setEditText(oneliner);
                }}
                onSave={() => saveEdit('oneliner')}
                onCancel={() => setEditing(null)}
                fontSize={21}
              />
            </div>

            {sections.map((s) => (
              <div
                key={s.key}
                style={{ padding: '13px 24px', borderBottom: '1px solid var(--line2)' }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                >
                  <Mono size={10.5}>{s.label}</Mono>
                  {s.skipped && (
                    <span
                      style={{
                        background: 'var(--amber-soft)',
                        border: '1px solid var(--amber-line)',
                        borderRadius: 20,
                        padding: '2px 8px',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 9.5,
                        letterSpacing: '.06em',
                        color: 'var(--amber)',
                      }}
                    >
                      {SKIPPED_BADGE}
                    </span>
                  )}
                </div>
                <EditableRow
                  editing={editing === s.key}
                  text={s.skipped ? SKIPPED_SECTION_TEXT : s.text}
                  muted={s.skipped}
                  truncate
                  value={editText}
                  onValue={setEditText}
                  onEdit={() => {
                    setEditing(s.key);
                    setEditText(s.skipped ? '' : s.text);
                  }}
                  onSave={() => saveEdit(s.key)}
                  onCancel={() => setEditing(null)}
                />
              </div>
            ))}
          </div>

          {/* Auth + consent + scan — one card, no navigation away. */}
          <div
            style={{
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              padding: 24,
              position: 'sticky',
              top: 96,
              boxShadow: '0 1px 2px rgba(24,23,32,.05)',
            }}
          >
            {submitting ? (
              // The scan takes up to ~40s; give the wait a scene, not a button
              // spinner. role="status" so screen readers announce the switch.
              <div role="status" style={{ textAlign: 'center', padding: '20px 0 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <ScanningMark size={44} />
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 600,
                    letterSpacing: '-.02em',
                    margin: '18px 0 0',
                  }}
                >
                  Scanning your idea…
                </h2>
                <p
                  style={{
                    fontSize: 14,
                    color: 'var(--muted)',
                    lineHeight: 1.6,
                    margin: '8px 0 0',
                  }}
                >
                  Comparing it against {COHORT_SIZE} real companies whose
                  outcomes we know. About 40 seconds — keep this tab open.
                </p>
              </div>
            ) : signedIn ? (
              <>
                <Mono size={10.5}>NEXT · RUN THE SCAN</Mono>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 600,
                    letterSpacing: '-.02em',
                    margin: '8px 0 0',
                  }}
                >
                  Your percentile, in about 40 seconds.
                </h2>
                <p
                  style={{
                    fontSize: 14.5,
                    color: 'var(--muted)',
                    lineHeight: 1.6,
                    margin: '8px 0 18px',
                  }}
                >
                  That inbox is also where the report lands within 48 hours.
                </p>

                <label
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: 'var(--ink2)',
                    cursor: 'pointer',
                    marginBottom: 16,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => {
                      setConsent(e.target.checked);
                      if (e.target.checked) setConsentError(false);
                    }}
                    style={{ marginTop: 3, accentColor: 'var(--acc)', width: 16, height: 16 }}
                  />
                  <span>
                    I agree to the privacy policy and to IdeaBrief processing
                    this idea to produce my report.
                  </span>
                </label>

                {consentError && (
                  <p
                    style={{
                      fontSize: 13.5,
                      color: 'oklch(0.55 0.19 25)',
                      margin: '0 0 12px',
                    }}
                  >
                    Please accept the privacy policy to continue.
                  </p>
                )}

                <Button
                  full
                  size="lg"
                  onClick={submitSignedIn}
                  // 50% opacity but still clickable, so it can surface the
                  // reason rather than silently doing nothing.
                  style={{ opacity: consent ? 1 : 0.5 }}
                >
                  Run the scan on my idea
                </Button>
              </>
            ) : (
              <AuthForm
                mode={authMode}
                onModeChange={setAuthMode}
                forgot={authForgot}
                onForgotChange={setAuthForgot}
                onAuthenticated={() => {
                  // Consent was just ticked on this form; mirror it so a scan
                  // failure leaves the signed-in card ready to retry without
                  // asking for the same agreement twice.
                  setConsent(true);
                  void runScan();
                }}
                consentScope="both"
                consentLabel={
                  <>
                    I agree to the <a href="/privacy">privacy policy</a> and{' '}
                    <a href="/terms">terms</a>, and to IdeaBrief processing this
                    idea to produce my report.
                  </>
                }
                submitLabel={{
                  up: 'Create account & run the scan',
                  in: 'Sign in & run the scan',
                }}
                resetRedirectPath="/auth/reset?next=launch"
                header={
                  <>
                    <Mono size={10.5}>
                      {authForgot
                        ? 'RESET YOUR PASSWORD'
                        : authMode === 'up'
                          ? 'LAST STEP · CREATE YOUR ACCOUNT'
                          : 'WELCOME BACK · SIGN IN'}
                    </Mono>
                    <h2
                      style={{
                        fontSize: 20,
                        fontWeight: 600,
                        letterSpacing: '-.02em',
                        margin: '8px 0 0',
                      }}
                    >
                      {authForgot
                        ? "We'll email you a reset link."
                        : 'Your percentile, in about 40 seconds.'}
                    </h2>
                    <p
                      style={{
                        fontSize: 14.5,
                        color: 'var(--muted)',
                        lineHeight: 1.6,
                        margin: '8px 0 0',
                      }}
                    >
                      {authForgot
                        ? 'Your brief stays saved on this device while you reset.'
                        : authMode === 'up'
                          ? 'One click creates your account and runs the scan. That inbox is also where the report lands within 48 hours.'
                          : 'Sign in and the scan runs straight away.'}
                    </p>
                  </>
                }
                variant="embedded"
              />
            )}

            {error && (
              <p
                style={{
                  fontSize: 13.5,
                  color: 'oklch(0.55 0.19 25)',
                  margin: '12px 0 0',
                  lineHeight: 1.5,
                }}
              >
                {error}
              </p>
            )}

            {!submitting && (
              <>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '18px 0 0',
                    fontSize: 13,
                    color: 'var(--muted)',
                    lineHeight: 1.7,
                  }}
                >
                  <li>The scan is instant. The report follows within 48 hours.</li>
                  <li>One idea per report — submit as many as you like.</li>
                  <li>We never spam, and never share your idea.</li>
                </ul>

                <button
                  onClick={() => navigate('/capture')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    marginTop: 16,
                    fontSize: 13.5,
                    color: 'var(--muted)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Keep refining
                </button>
              </>
            )}
          </div>
        </div>
      </main>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--ink)',
            color: '#fff',
            borderRadius: 20,
            padding: '10px 18px',
            fontSize: 13.5,
            zIndex: 20,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

interface RowProps {
  editing: boolean;
  text: string;
  muted?: boolean;
  /** Clip to a single line with an ellipsis. Off for the one-liner headline. */
  truncate?: boolean;
  value: string;
  fontSize?: number;
  onValue: (v: string) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}

function EditableRow({
  editing,
  text,
  muted,
  truncate,
  value,
  fontSize = 14,
  onValue,
  onEdit,
  onSave,
  onCancel,
}: RowProps) {
  if (editing) {
    return (
      <div style={{ marginTop: 6 }}>
        <textarea
          value={value}
          onChange={(e) => onValue(e.target.value)}
          autoFocus
          rows={3}
          style={{
            width: '100%',
            border: '1px solid var(--acc)',
            borderRadius: 10,
            padding: 10,
            fontSize,
            fontFamily: 'inherit',
            lineHeight: 1.55,
            resize: 'vertical',
            outline: 'none',
            color: 'var(--ink)',
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button size="sm" onClick={onSave}>
            Save
          </Button>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onEdit}
      role="button"
      tabIndex={0}
      title={truncate ? text : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize,
        lineHeight: 1.55,
        color: muted ? 'var(--muted)' : 'var(--ink2)',
        cursor: 'pointer',
        borderRadius: 8,
        padding: '2px 4px',
        margin: '6px -4px 0',
      }}
    >
      {/* Section rows clip to a single line with an ellipsis — the brief is
          meant to be glanceable, and full text lives in the editor. The
          one-liner is exempt: it is the headline and must read in full. */}
      <span
        style={
          truncate
            ? {
                flex: 1,
                minWidth: 0,
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }
            : { flex: 1, minWidth: 0 }
        }
      >
        {text || <span style={{ color: 'var(--muted)' }}>—</span>}
      </span>
      <span
        aria-hidden="true"
        style={{ flex: 'none', color: 'var(--muted)', fontSize: 12 }}
      >
        ✎
      </span>
    </div>
  );
}
